import { useEffect, useRef, useState } from "react";

import { deriveDisplayPrice } from "../../../lib/display-price.ts";

import type {
	OrderbookLevel,
	TradePriceDirection,
} from "../types/orderbook.types.ts";

type OrderbookSnapshot = {
	levels: [OrderbookLevel[], OrderbookLevel[]];
};

type TradeTick = {
	px: string | number | null | undefined;
	time?: number;
};

type UseOrderbookParams = {
	book: OrderbookSnapshot | null | undefined;
	trades: TradeTick[];
	markPrice: number;
	marketKey: string;
	rowLimit: number;
	/**
	 * The grouping to use for formatting the rows in `book`. Callers must pass
	 * the grouping that the *displayed* snapshot was aggregated under (falling
	 * back to the requested grouping when no snapshot has arrived yet) so that
	 * `priceDecimals` and bucket indices always match the rows on screen.
	 */
	grouping: GroupingOption;
};

export type ProcessedLevel = {
	px: string;
	sz: number;
	total: number;
	depth: number;
	szUsdc: number;
	totalUsdc: number;
	bucketIndex: number;
};

/**
 * Map a raw price to its discrete bucket index given a step size. Bids round
 * down, asks round up — this matches how Hyperliquid's server-side aggregation
 * assigns levels and lets us key rows on a value that is stable across ticks
 * within a single grouping but distinct between bids/asks at the spread.
 */
export function bucketIndexForOrder(
	price: number,
	side: "bid" | "ask",
	step: number,
): number | null {
	if (
		!Number.isFinite(price) ||
		price <= 0 ||
		!Number.isFinite(step) ||
		step <= 0
	) {
		return null;
	}
	const scaled = price / step;
	return side === "bid"
		? Math.floor(scaled + Number.EPSILON)
		: Math.ceil(scaled - Number.EPSILON);
}

// --- Grouping options ---

export type GroupingOption = {
	label: string;
	nSigFigs: 2 | 3 | 4 | 5;
	mantissa?: 2 | 5;
};

const MAX_GROUPING_LABEL_FRACTION_DIGITS = 20;

/**
 * Generate grouping options based on price.
 * Maps each option to the Hyperliquid nSigFigs/mantissa params.
 *
 * nSigFigs controls rounding to N significant figures:
 *   BTC $73,789: nSigFigs=5 → step 1, =4 → step 10, =3 → step 100, =2 → step 1000
 *   ETH  $2,249: nSigFigs=5 → step 0.1, =4 → step 1, =3 → step 10, =2 → step 100
 *   SOL    $130: nSigFigs=5 → step 0.01, =4 → step 0.1, =3 → step 1, =2 → step 10
 *
 * mantissa (only with nSigFigs=5): groups last sig fig by 2 or 5
 */
export function getGroupingStep(grouping: GroupingOption): number {
	return Number(grouping.label.replaceAll(",", ""));
}

export function getLabelPrecision(label: string): number {
	const normalized = label.replaceAll(",", "");
	const step = Number(normalized);
	if (!Number.isFinite(step) || step <= 0) return 0;
	const stepLabel = normalized.toLowerCase();
	const fraction = stepLabel.split(".")[1];
	return fraction?.length ?? 0;
}

function formatStep(step: number): string {
	return step.toLocaleString("en-US", {
		useGrouping: step >= 1,
		maximumFractionDigits: MAX_GROUPING_LABEL_FRACTION_DIGITS,
	});
}

export function getGroupingOptions(price: number): GroupingOption[] {
	if (price <= 0) return [{ label: "0.01", nSigFigs: 5 }];

	const magnitude = Math.floor(Math.log10(price));

	function stepForSigFigs(n: number, mantissa?: number): number {
		// The step size = 10^(magnitude - n + 1) * (mantissa ?? 1)
		const step = 10 ** (magnitude - n + 1) * (mantissa ?? 1);
		return step;
	}

	const options: GroupingOption[] = [];

	// nSigFigs: 5 (finest), then 5 with mantissa 2, 5 with mantissa 5, then 4, 3, 2
	const configs: { nSigFigs: 2 | 3 | 4 | 5; mantissa?: 2 | 5 }[] = [
		{ nSigFigs: 5 },
		{ nSigFigs: 5, mantissa: 2 },
		{ nSigFigs: 5, mantissa: 5 },
		{ nSigFigs: 4 },
		{ nSigFigs: 3 },
		{ nSigFigs: 2 },
	];

	const maxGrouping = price * 0.02; // Cap at ~2% of price

	for (const config of configs) {
		const step = stepForSigFigs(config.nSigFigs, config.mantissa);
		if (step > 0 && step <= maxGrouping) {
			options.push({
				label: formatStep(step),
				nSigFigs: config.nSigFigs,
				mantissa: config.mantissa,
			});
		}
	}

	if (options.length === 0) {
		options.push({ label: "1", nSigFigs: 5 });
	}

	return options;
}

// --- Hook ---

export function useOrderbook({
	book,
	trades,
	markPrice,
	marketKey,
	rowLimit,
	grouping,
}: UseOrderbookParams) {
	const rawBids = book?.levels[0] ?? [];
	const rawAsks = book?.levels[1] ?? [];

	// Display precision is derived from the grouping step the server used,
	// not sampled from the data — sampling can collapse distinct levels to
	// the same string and produce phantom bid/ask price collisions.
	const priceDecimals = getLabelPrecision(grouping.label);

	const step = getGroupingStep(grouping);

	// Use the API's canonical px string directly. Round-tripping through
	// Number + toFixed risks aliasing two distinct buckets to the same label.
	function processLevels(
		levels: OrderbookLevel[],
		side: "bid" | "ask",
	): ProcessedLevel[] {
		let cumulative = 0;
		let cumulativeUsdc = 0;
		return levels.slice(0, rowLimit).map((level, idx) => {
			const sz = Number(level.sz);
			const price = Number(level.px);
			cumulative += sz;
			const szUsdc = sz * price;
			cumulativeUsdc += szUsdc;
			// Fall back to a synthetic per-side index if the price can't be
			// bucketed (NaN, zero step) so React still gets a stable key.
			const bucketIndex =
				bucketIndexForOrder(price, side, step) ??
				(side === "bid" ? -1 - idx : -1_000_000 - idx);
			return {
				px: level.px,
				sz,
				total: cumulative,
				depth: 0,
				szUsdc,
				totalUsdc: cumulativeUsdc,
				bucketIndex,
			};
		});
	}

	const bids = processLevels(rawBids, "bid");
	const asks = processLevels(rawAsks, "ask");

	const maxBidTotal = bids.length > 0 ? bids[bids.length - 1].total : 1;
	const maxAskTotal = asks.length > 0 ? asks[asks.length - 1].total : 1;

	for (const bid of bids) {
		bid.depth = (bid.total / maxBidTotal) * 100;
	}
	for (const ask of asks) {
		ask.depth = (ask.total / maxAskTotal) * 100;
	}

	// Spread from raw best bid/ask
	const bestBid = rawBids[0] ? Number(rawBids[0].px) : 0;
	const bestAsk = rawAsks[0] ? Number(rawAsks[0].px) : 0;
	const spread = bestAsk && bestBid ? bestAsk - bestBid : 0;
	const spreadPercent =
		bestAsk && bestBid ? ((spread / bestAsk) * 100).toPrecision(2) : "0.00";

	// Trade price direction. Show the last *traded* price while the feed is
	// live, but fall back to mark when the latest trade is stale (illiquid
	// markets) — see lib/display-price.ts. Shares the rule with the tab title
	// so the orderbook center and the browser tab never show conflicting prices.
	const { price: lastTradedPrice } = deriveDisplayPrice({
		trades,
		markPrice,
		nowMs: Date.now(),
	});
	const previousTradePriceRef = useRef(lastTradedPrice);
	const previousMarketKeyRef = useRef(marketKey);
	const [tradePriceDirection, setTradePriceDirection] =
		useState<TradePriceDirection>("flat");

	useEffect(() => {
		if (previousMarketKeyRef.current !== marketKey) {
			previousMarketKeyRef.current = marketKey;
			previousTradePriceRef.current = lastTradedPrice;
			setTradePriceDirection("flat");
			return;
		}

		if (lastTradedPrice > previousTradePriceRef.current) {
			setTradePriceDirection("up");
		} else if (lastTradedPrice < previousTradePriceRef.current) {
			setTradePriceDirection("down");
		} else {
			setTradePriceDirection("flat");
		}
		previousTradePriceRef.current = lastTradedPrice;
	}, [lastTradedPrice, marketKey]);

	const tradePriceArrow =
		tradePriceDirection === "up"
			? "↑"
			: tradePriceDirection === "down"
				? "↓"
				: "•";
	const tradePriceClassName =
		tradePriceDirection === "up"
			? "text-trade-green"
			: tradePriceDirection === "down"
				? "text-trade-red"
				: "text-trade-text-secondary";

	return {
		bids,
		asks,
		spread,
		spreadPercent,
		lastTradedPrice,
		tradePriceArrow,
		tradePriceClassName,
		priceDecimals,
	};
}
